import {EDesignTemplates} from 'components/table-report/table-report-types';
import {TPostgresColumnType, EPostgresDateType, EPostgresNumberType} from 'shared/types/postgres';
import {TReportConfigurationDataConverted} from 'store/slices/report-configuration-slice';
import {
    TReportConfigurationCustomColumnDto,
    TReportConfigurationAlign,
    TReportConfigurationExcelHeaderTableStructureDto,
    IReportConfigurationReportColumnDto, TReportConfigurationFontStyle,
    IReportConfigurationReportGroupDto,
} from 'store/slices/report-configuration-slice/report-configuration-dto';
import {TReportConfigurationConvertedField} from 'store/slices/report-configuration-slice/report-configuration-types';

import {TTableAttributesCustomSelectEntry} from '../report-table-settings';

interface IDefaultTableAttributesValues {
  align: TReportConfigurationAlign;
  style: TReportConfigurationFontStyle;
  format?: string;
}

export const getDefaultTableAttributesValues = (
    fieldType: TPostgresColumnType,
): IDefaultTableAttributesValues => {
    switch (fieldType) {
    case EPostgresNumberType.numeric:
        return {align: 'RIGHT_MIDDLE', style: 'FONT_NORMAL', format: '#,##0.00'};
    case EPostgresDateType.date:
    case EPostgresDateType.timestamp:
        return {align: 'LEFT_MIDDLE', style: 'FONT_NORMAL', format: 'dd.MM.yyyy'};
    case EPostgresNumberType.int:
    case EPostgresNumberType.int2:
    case EPostgresNumberType.int4:
    case EPostgresNumberType.int8:
        return {align: 'RIGHT_MIDDLE', style: 'FONT_NORMAL', format: '#'};
    default:
        return {align: 'LEFT_MIDDLE', style: 'FONT_NORMAL'};
    }
};

export const isViewModeSwitchSupported = (design: string | undefined) => (
    design === EDesignTemplates.TABLE_REPORT_DESIGN_TEMPLATE_WITH_PARAMETER_AND_HEADER
);

export const mergeTableAttributesAndReportColumns = (
    attributesEntries: TTableAttributesCustomSelectEntry[],
    reportColumns: TReportConfigurationDataConverted['reportColumns'],
) => attributesEntries?.map(attribute => {
    const entryFromData = reportColumns?.find(col => col?.keyName === attribute?.value);
    const {align, style, format} = getDefaultTableAttributesValues(attribute?.type as TPostgresColumnType);
    return {
        keyName: attribute?.value,
        fieldType: attribute?.type,
        typeGroup: attribute?.typeGroup,
        reportTitle: entryFromData?.reportTitle ?? attribute?.comment,
        columnOrdinal: entryFromData?.columnOrdinal,
        hidden: entryFromData?.hidden,
        fieldFunction: entryFromData?.fieldFunction,
        // formatting form fields
        align: entryFromData?.align ?? align,
        style: entryFromData?.style ?? style,
        format: entryFromData?.format ?? format,
        width: entryFromData?.width,
        enabledTextWrapping: entryFromData?.enabledTextWrapping,
        // security modal fields
        blockedForRoleAndFunctionNames: entryFromData?.blockedForRoleAndFunctionNames,
        closeFieldWhenChangingMode: entryFromData?.closeFieldWhenChangingMode,
    };
});

/**
 * Устанавливает Порядок вывода начиная с 1
 * для каждого столбца в массивах основных атрибутов и кастомных (пользовательских) атрибутов.
 *
 * @param reportColumnsToModify - Массив атрибутов, которые нужно модифицировать.
 * @param customColumnsToModify - Массив пользовательских атрибутов, которые нужно модифицировать.
 * @param skipHidden - Если `true`, то столбцы которые не выводятся в отчет (`hidden: true`) пропускаются.
 *
 * @returns Объект с массивами `reportColumns` и `customColumns`,
 * в которых для каждого столбца установлен `columnOrdinal`.
 */
export function setOrdinalNumber(
    reportColumnsToModify: IReportConfigurationReportColumnDto[] | undefined,
    customColumnsToModify: TReportConfigurationCustomColumnDto[] | undefined,
    skipHidden: boolean,
) {
    let acc = 1;

    if (reportColumnsToModify) {
        for (let i = 0; i < reportColumnsToModify.length; i += 1) {
            if (!(skipHidden && reportColumnsToModify[i].hidden)) {
                reportColumnsToModify[i].columnOrdinal = acc;
                acc += 1;
            }
        }
    }

    if (customColumnsToModify) {
        for (let i = 0; i < customColumnsToModify.length; i += 1) {
            if (!(skipHidden && customColumnsToModify[i].hidden)) {
                customColumnsToModify[i].columnOrdinal = acc;
                acc += 1;
            }
        }
    }

    return {reportColumns: reportColumnsToModify, customColumns: customColumnsToModify};
}

interface IDataToJoin {
    reportColumns?: TReportConfigurationDataConverted['reportColumns'];
    customColumns?: TReportConfigurationDataConverted['customColumns'];
    excelTableHeaderStructure?: TReportConfigurationExcelHeaderTableStructureDto;
}

interface IJoinReportTableDataToMenuConfigurationDataArgs {
    reportMenu: TReportConfigurationDataConverted['reportMenu'];
    dataToJoin: IDataToJoin;
    sheetCode?: string;
}

type TReportMenu = TReportConfigurationDataConverted['reportMenu'];
type TReportConfigurationMenuItem = Exclude<TReportMenu, undefined>[0];

/* Рекурсивно проходим по всей структуре меню, к нужному объекту [при совпадении sheetCode]
   прикрепляем переданные reportColumns. */
export const joinReportTableDataToMenuConfigurationData = ({
    reportMenu,
    dataToJoin,
    sheetCode,
}: IJoinReportTableDataToMenuConfigurationDataArgs) => {
    if (!sheetCode) return reportMenu;
    const join: (menu: TReportMenu) => TReportMenu = menu => (
        menu?.map((item: TReportConfigurationMenuItem) => {
            if (!item) return item;
            if (item.sheetCode === sheetCode) {
                return {
                    ...item,
                    ...dataToJoin,
                } as TReportConfigurationMenuItem;
            }
            if (item.children) {
                return {
                    ...item,
                    children: join(item.children),
                } as TReportConfigurationMenuItem;
            }
            return item;
        })
    );
    return join(reportMenu);
};

/**
 * Переименовывает повторяющиеся значения ключей `keyName` в массиве объектов.
 * Если ключ `keyName` встречается более одного раза, к его имени добавляется
 * уникальный суффикс в формате `$<порядковый номер повторения>`.
 *
 * @param arr - Исходный массив объектов, содержащих поля с ключами для проверки дубликатов.
 * @returns Новый массив объектов с уникальными значениями `keyName`.
 *
 * @example
 * ```ts
 * const input = [
 *   { keyName: 'name', hierarchyLevel: 1 },
 *   { keyName: 'name', hierarchyLevel: 2 },
 *   { keyName: 'age', hierarchyLevel: 3 },
 *   { keyName: 'name', hierarchyLevel: 4 }
 * ];
 *
 * const result = renameDuplicateKeyNames(input);
 * ```
 *
 * Результат:
 * ```ts
 * [
 *    { keyName: 'name', hierarchyLevel: 1 },
 *    { keyName: 'name$1', hierarchyLevel: 2 },
 *    { keyName: 'age', hierarchyLevel: 3 },
 *    { keyName: 'name$2', hierarchyLevel: 4 }
 * ]
 * ```
 */
export const renameDuplicateKeyNames = (
    arr: TReportConfigurationConvertedField<IReportConfigurationReportGroupDto>,
): TReportConfigurationConvertedField<IReportConfigurationReportGroupDto> => {
    const keyCount: Record<string, number> = {};
    const result: TReportConfigurationConvertedField<IReportConfigurationReportGroupDto> = [];

    for (let i = 0; i < arr.length; i += 1) {
        const key = arr[i].keyName;

        if (keyCount[key] === undefined) {
            keyCount[key] = 0;
            result.push(arr[i]);
        } else {
            keyCount[key] += 1;
            result.push({
                ...arr[i],
                keyName: `${key}$${keyCount[key]}`,
            });
        }
    }

    return result;
};

/**
 * Удаляет постфиксы в формате `$<порядковый номер>` из значений `keyName` в массиве объектов.
 * Если в значении `keyName` отсутствует символ `$`, значение остается без изменений.
 *
 * @param arr - Массив объектов, содержащих ключи `keyName` для обработки.
 * @returns Новый массив объектов с очищенными значениями `keyName`.
 *
 * @example
 * ```ts
 * const input = [
 *   { keyName: 'name$1', hierarchyLevel: 1 },
 *   { keyName: 'name$2', hierarchyLevel: 2 },
 *   { keyName: 'age', hierarchyLevel: 3 }
 * ];
 * const result = removeKeyNamesPostfix(input);
 * ```
 * Результат:
 * ```ts
 * [
 *   { keyName: 'name', hierarchyLevel: 1 },
 *   { keyName: 'name', hierarchyLevel: 2 },
 *   { keyName: 'age', hierarchyLevel: 3 }
 * ]
 * ```
 */
export const removeKeyNamesPostfix = (
    arr: TReportConfigurationConvertedField<IReportConfigurationReportGroupDto>,
): TReportConfigurationConvertedField<IReportConfigurationReportGroupDto> => arr.map(item => {
    const dollarIndex = item.keyName.indexOf('$');
    return {
        ...item,
        keyName: dollarIndex !== -1 ? item.keyName.substring(0, dollarIndex) : item.keyName,
    };
});
